// formOptions.ts

import { ISkeletonField } from "common/skeleton";
import { ENVIRONMENT } from "const";

// --- Classification Options ---
export const classifications = [
    { value: "INTERNAL", label: "INTERNAL" },
    { value: "RESTRICTED", label: "RESTRICTED" },
    { value: "SECRET", label: "SECRET" },
];

export const IDEATION_HUB_DISPLAY_NAME = "Ideation Hub";

export const DEFAULT_CLASSIFICATIONS = ["SECRET", "RESTRICTED", "INTERNAL"];

export function isIdeationHubDomain(domainName?: string | null): boolean {
    return (domainName ?? "").trim().toLowerCase() === IDEATION_HUB_DISPLAY_NAME.toLowerCase();
}

/** Ideation Hub must not offer SECRET. Other domains keep the list they were given. */
export function classificationsForPrimaryDomain(
    domainName: string | undefined | null,
    options: string[],
): string[] {
    if (!isIdeationHubDomain(domainName)) {
        return options;
    }
    return options.filter((value) => value.trim().toUpperCase() !== "SECRET");
}

// --- Licensing Obligation Options ---
export const licensingObligation = [
    { value: "Yes", label: "YES" },
    { value: "No", label: "NO" },
];

// --- Retention Terms Options ---
export const retentionTerms = [
    { value: "short", label: "Short term (3 years)" },
    { value: "mid", label: "Mid term (6 years)" },
    { value: "long", label: "Long term (9 years)" },
    { value: "gxp_long", label: "Clinical study (25 years)" },
];

export const retentionTermsExtended = [
    { value: "two_weeks", label: "Two Weeks (14 days)" },
];

// --- Per-Environment Retention Policy ---
// Maps environment name → allowed retention types and their allowed terms.
// If an environment is not listed, all retention types/terms are available (unrestricted).
// To restrict another env, just add an entry here — no code changes needed.

export interface RetentionPolicy {
    allowedTypes: Array<"period" | "date" | "experimentperiod">;
    allowedTerms: Record<string, string[]>;  // type → allowed term values
    defaultType: "period" | "date" | "experimentperiod";
    defaultTerm: string;
}

export const retentionPolicies: Record<string, RetentionPolicy> = {
    dev: {
        allowedTypes: ["experimentperiod"],
        allowedTerms: { experimentperiod: ["two_weeks"] },
        defaultType: "experimentperiod",
        defaultTerm: "two_weeks",
    },
};

// --- BYOC RDS Database Type Options ---
// Single source of truth for the database types a user can provision in the
// BYOC flow. To support an additional type in the future, just add a new
// entry to this array — the UI selector and payload handling pick it up
// automatically with no further code changes.
export const RDS_TYPE_DEFAULT = "aurora_postgresql";

export const rdsTypeOptions = [
    { value: "aurora_postgresql", label: "Aurora PostgreSQL" },
    { value: "mariadb", label: "MariaDB" },
];

// --- ID Compliance Options ---
// Shown only for the "External Data Exchange" domain (create and edit). The
// selected value is sent as `id_compliance` at the root of the space payload
// (outside metadata). Selection is required for this domain.
export const idComplianceOptions = [
    { value: "DPS-Compliant", label: "DPS COMPLIANT" },
    { value: "Non-Verified", label: "NON VERIFIED" },
];

// GPU-enabled SageMaker instances:
export const gpuInstances = [
  {
    instance: "ml.g4dn.xlarge",
    memory: "16 GiB",
    gpu: "1 × NVIDIA T4",
    storage: "125 GB NVMe SSD",
    cost: "$0.526/hr (~$537.28/month)",
  },
  {
    instance: "ml.g4dn.2xlarge",
    memory: "32 GiB",
    gpu: "1 × NVIDIA T4",
    storage: "225 GB NVMe SSD",
    cost: "$0.752/hr (~$686.20/month)",
  },
  {
    instance: "ml.g4dn.4xlarge",
    memory: "64 GiB",
    gpu: "1 × NVIDIA T4",
    storage: "225 GB NVMe SSD",
    cost: "$1.204/hr (~$1,098.65/month)",
  },
  {
    instance: "ml.g4dn.8xlarge",
    memory: "128 GiB",
    gpu: "1 × NVIDIA T4",
    storage: "900 GB NVMe SSD",
    cost: "$2.176/hr (~$1,985.60/month)",
  },
  {
    instance: "ml.g4dn.12xlarge",
    memory: "192 GiB",
    gpu: "4 × NVIDIA T4",
    storage: "900 GB NVMe SSD",
    cost: "$3.912/hr (~$3,569.70/month)",
  },
  {
    instance: "ml.g4dn.16xlarge",
    memory: "256 GiB",
    gpu: "1 × NVIDIA T4",
    storage: "900 GB NVMe SSD",
    cost: "$4.176/hr (~$3,971.20/month)",
  },
];


// --- Profile Options Map ---
// Object contains label, value, and estimated time (in minutes) for creating a space for each profile type.
export const profileOptionsMap: Record<string, { value: string; label: string; estimated_time?: string }[]> = {
    data: [
        { value: "store_explore", label: "Store & Explore", estimated_time: "10" },
        { value: "data_product_eng", label: "Data Product Eng", estimated_time: "20 - 30"  },
    ],
    analytics: [
        { value: "store_analyze", label: "Store & Analyze", estimated_time: "10" },
        ...(ENVIRONMENT !== "prod" && ENVIRONMENT !== "qa" ? [{
            value: "analytics_hpc",
            label: "Analytics on HPC",
            estimated_time: "10",
        }] : []),
        { value: "ml_feature_eng", label: "ML Feature Eng", estimated_time: "20 - 30" },
        { value: "ml_model_eng", label: "ML Model Eng", estimated_time: "20 - 30" },
        { value: "gen_ai_eng", label: "Gen AI Eng", estimated_time: "10 - 15" },
        // { value: "advanced_fm", label: "Advanced FM on Hyperpod" }, // TASK:503971
        
    ],
    dse_app: [
        { value: "app_deployment_(BYOC)", label: "App Deployment (BYOC)", estimated_time: "10" }, 
    ],
};

console.log("enqvironment in constants:", ENVIRONMENT);


export const skeletonData: ISkeletonField[][] = [
    // Row 1: Two inputs side by side
    [
        { type: "input", span: 12 },
        { type: "input", span: 12 },
    ],
    // Row 2: Full-width radio group
    [
        { type: "radio", span: 24 },
    ],
    // Row 3: Full-width textarea
    [
        { type: "textarea", span: 24 },
    ],
    // Row 4: Two selects
    [
        { type: "select", span: 12 },
        { type: "select", span: 12 },
    ],
    // Row 5: One input full width
    [
        { type: "input", span: 24 },
    ],
    // Row 6: Two smaller inputs
    [
        { type: "input", span: 8 },
        { type: "input", span: 8 },
        { type: "input", span: 8 },
    ],
];

export const commonProject = { project_id: "1", project_name: "Common Project" };

export const commonStudy = { study_id: "1", study_name: "Common Study", project_id: "1", project_name: "Common Project" };
// 10 minutes
export const Space_Invalidation_period = 600000;

export const DO_NOT_EDIT_KEY = "do_not_edit_this_form" as const;
export const DO_NOT_EDIT_MESSAGE = "Changes made to this form will ALWAYS be overwritten with values provided in Nexus Explorer." as const;
export const GXP_VALIDATED_DOMAINS = ["Clinical Biomarker-OMICS-BMDA"]; // List of domains that have validated GxP processes

/** True when the space type is GxP. Custom File System is not offered for these spaces. */
export const isGxpSpaceType = (bucketType?: string | null): boolean =>
    (bucketType ?? "").trim().toLowerCase() === "gxp";

export const API_ERROR_MESSAGES: Record<number, string> = {
    401: "Your session has expired. Please log in again.",
    403: "You don't have permission to perform this action.",
    404: "The requested resource was not found. Please refresh and try again.",
    409: "A conflict occurred. The space may already exist or have conflicting settings.",
    422: "The submitted data is invalid. Please review your input and try again.",
    429: "Too many requests. Please wait a moment before trying again.",
    500: "An internal server error occurred. Please try again later.",
    502: "The service is temporarily unavailable. Please try again later.",
    503: "The service is temporarily unavailable. Please try again later.",
    504: "The request timed out. Please try again later.",
};

/** Returns a user-facing message for an HTTP status, preferring the API-provided message over the default. */
export const getHttpErrorMessage = (status: number, apiMessage?: string): string =>
    apiMessage || API_ERROR_MESSAGES[status] || `An unexpected error occurred (status: ${status}). Please try again.`;
